import type { BadgeView } from '@contracts';
import { BadgeGraphic } from '@ui/shared/badges/BadgeGraphic';
import { describeCriterion } from '@ui/shared/badges/criterionText';

/**
 * Badge gallery (settings-rewards.md §3, badges-and-celebrations.md §1) —
 * grid of badge compositions, earned (luminous) and locked (quiet outline +
 * progress) states, explicit criterion + status on every cell, no exceptions
 * (owner's standing transparency requirement). Sort: earned (newest first
 * proxy — this build has no earned-date-ordering signal beyond `earnedOn`
 * itself, used directly), then in-progress by percent complete, then
 * untouched (0 progress).
 *
 * DEFERRED_STARTER_BADGES handling: those badges simply never appear in
 * `listBadges()`'s result (AC-R13, enforced domain-side) — nothing here
 * needs to special-case them. The four ACTIVE starter badges plus any
 * user-created ones all correctly show "0 of N" when unearned, which reads
 * as a roadmap from minute one, not a bug (settings-rewards.md §7 "Fresh
 * install").
 */
export function BadgeGallery({
  badges,
  onSelect,
}: {
  readonly badges: readonly BadgeView[];
  readonly onSelect: (badge: BadgeView) => void;
}) {
  const sorted = [...badges].sort((a, b) => {
    if (a.state.earned !== b.state.earned) return a.state.earned ? -1 : 1;
    if (a.state.earned && b.state.earned) {
      return b.state.earnedOn.localeCompare(a.state.earnedOn);
    }
    if (!a.state.earned && !b.state.earned) {
      const pa = a.state.progress.target > 0 ? a.state.progress.current / a.state.progress.target : 0;
      const pb = b.state.progress.target > 0 ? b.state.progress.current / b.state.progress.target : 0;
      return pb - pa;
    }
    return 0;
  });

  if (sorted.length === 0) {
    return (
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
        No badges yet.
      </p>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
        gap: 'var(--space-4)',
      }}
    >
      {sorted.map((badge) => (
        <BadgeCell key={badge.badgeId} badge={badge} onSelect={() => onSelect(badge)} />
      ))}
    </div>
  );
}

function BadgeCell({
  badge,
  onSelect,
}: {
  readonly badge: BadgeView;
  readonly onSelect: () => void;
}) {
  const criterionText = describeCriterion(badge.criterion);
  const statusText = badge.state.earned
    ? 'Earned'
    : `${badge.state.progress.current} of ${badge.state.progress.target} done`;
  const progressFraction = badge.state.earned
    ? 1
    : badge.state.progress.target > 0
      ? Math.min(1, badge.state.progress.current / badge.state.progress.target)
      : 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`${badge.name}, ${criterionText}, ${badge.state.earned ? 'earned' : `in progress, ${statusText}`}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        textAlign: 'center',
      }}
    >
      <BadgeGraphic
        iconId={badge.iconId}
        earned={badge.state.earned}
        category={null}
        progressFraction={progressFraction}
        size={64}
      />
      <div>
        <div style={{ fontSize: 'var(--type-caption-size)', fontWeight: 500, color: 'var(--text-primary)' }}>
          {badge.name}
        </div>
        <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
          {criterionText}
        </div>
        <div
          className="num"
          style={{
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-muted)',
            marginTop: 'var(--space-1)',
          }}
        >
          {statusText}
        </div>
      </div>
    </button>
  );
}
